import { sendGTMEvent } from "@next/third-parties/google"
import React, { useEffect, useMemo, useState } from "react"

import { useAuthToken } from "@lib/hooks/use-auth-token"
import { sendGoogleAnalyticsEvent } from "@modules/common/utils/analytics"
import OnfidoVerification from "@modules/identity-verification/components/onfido"
import VerifyInAppScreen from "@modules/signup-and-order-flow/components/verify-in-app-screen"
import VerifyInWhatsappScreen from "@modules/signup-and-order-flow/components/verify-in-whatsapp-screen"
import WhatsappIdentityVerification from "@modules/signup-and-order-flow/components/whatsapp-identity-verification"
import { useSession } from "@modules/signup-and-order-flow/signup-and-order-flow-v2/session-context"
import useProductData from "@modules/signup-and-order-flow/util/use-fetch-product-data"
import useSendWhatsappLink from "@modules/signup-and-order-flow/util/use-send-onfido-link"
import { useGetProfile } from "@modules/signup-and-order-flow/util/useGetProfile"

import { StepProps } from "../step-registry"

enum VerificationView {
  ONFIDO = "Identity Verification web",
  IN_APP = "Continue verification in app",
  WHATSAPP = "Native Whatsapp Identity Verification",
  WHATSAPP_SENT = "whatsapp_verification_link_sent",
}

const PURCHASE_INTENT_INTERSTITIAL_LINK = "https://6i6nb.app.link/9KiyKmevDRb"
const APP_DOWNLOAD_LINK = "https://a.nelo.mx/ZpywgahKiMb"

const IDVerificationStep: React.FC<StepProps> = ({ onComplete, onBack }) => {
  const { sessionData } = useSession()
  const authToken = useAuthToken()

  const { signupCompleted } = sessionData.progress
  const { treatments } = sessionData
  const productId = sessionData.product.productId

  const [currentView, setCurrentView] = useState<VerificationView | null>(null)

  const { product } = useProductData(productId)
  const { profile } = useGetProfile()
  const { sendWhatsappVerificationLink } = useSendWhatsappLink()

  const truncatedProductTitle = product?.title ? (product.title.length > 20 ? `${product.title.slice(0, 20)}...` : product.title) : ""

  const appDownloadLink = useMemo(() => {
    try {
      const intentLink = new URL(PURCHASE_INTENT_INTERSTITIAL_LINK)
      intentLink.searchParams.append("phone", profile?.phoneNumber || "")
      intentLink.searchParams.append("email", profile?.email || "")
      intentLink.searchParams.append("product_thumbnail", product?.thumbnail || "")
      intentLink.searchParams.append("product_title", product?.title || "")

      return treatments.mktp_purchase_intent_interstitial.config ? intentLink.toString() : APP_DOWNLOAD_LINK
    } catch (error) {
      console.error("Error creating purchase intent interstitial link:", error)
      return APP_DOWNLOAD_LINK
    }
  }, [profile, product, treatments.mktp_purchase_intent_interstitial.config])

  useEffect(() => {
    const verificationPath = treatments.mktp_id_verification_path?.config

    let view: VerificationView
    if (verificationPath === "whatsapp") {
      view = VerificationView.WHATSAPP
    } else if (verificationPath === "web" && authToken) {
      view = VerificationView.ONFIDO
    } else {
      view = VerificationView.IN_APP
    }
    setCurrentView(view)

    // Analytics
    sendGoogleAnalyticsEvent("XP_INCLUSION", {
      featureName: "mktp_id_verification_path",
      trackingKey: "mktp_id_verification_path_experiment",
      treatmentName: verificationPath,
    })
  }, [treatments, authToken])

  useEffect(() => {
    if (!currentView) return
    sendGTMEvent({ event: currentView })
    sendGoogleAnalyticsEvent("VERIFICATION_STACK", { step: currentView })
  }, [currentView])

  const handleVerifyInApp = () => setCurrentView(VerificationView.IN_APP)

  const renderContent = () => {
    if (!signupCompleted || !profile) {
      return <div>Loading...</div>
    }

    switch (currentView) {
      case VerificationView.ONFIDO:
        return <OnfidoVerification authToken={authToken!} onComplete={onComplete} />

      case VerificationView.IN_APP:
        return <VerifyInAppScreen appDownloadLink={appDownloadLink} />

      case VerificationView.WHATSAPP:
        return (
          <WhatsappIdentityVerification
            userProfile={profile}
            sendWhatsappLink={sendWhatsappVerificationLink}
            downloadApp={handleVerifyInApp}
            handleCloseSheet={onBack}
            productTitle={truncatedProductTitle}
          />
        )

      case VerificationView.WHATSAPP_SENT:
        return <VerifyInWhatsappScreen />

      default:
        return <div>Loading...</div>
    }
  }

  return <div>{renderContent()}</div>
}

export default IDVerificationStep
